import { FC, memo, useCallback, useEffect, useRef } from "react";
import type { SearchMode } from "~/features/skyView/store/searchHighlight.slice";
import { BoundingBoxMatch } from "../../hooks/useTextBoundingBoxes";

type TextHighlightOverlayProps = {
  matches: BoundingBoxMatch[];
  rotation: number;
  mode?: SearchMode;
  transform?: string;
  transformOrigin?: string;
};

const CANVAS_SIZE = 1000;

// Heatmap style (exact search)
const HEATMAP_GRADIENT_STOPS: Array<[number, string]> = [
  [0, "rgba(255, 200, 0, 0.3)"],
  [0.3, "rgba(255, 180, 0, 0.25)"],
  [0.6, "rgba(255, 100, 0, 0.01)"],
  [1, "rgba(255, 50, 0, 0)"],
];
const HEATMAP_RADIUS_MULTIPLIER = 1;

// Box style (context / citation click) — slight blur for a softer look
const BOX_FILL = "rgba(255, 200, 0, 0.30)";
const BOX_STROKE = "rgba(255, 160, 0, 0.55)";
const BOX_LINE_WIDTH = 2;
const BOX_BORDER_RADIUS = 3;
const BOX_BLUR_PX = 4;

function transformBoxForRotation(
  box: [number, number, number, number],
  rotation: number
): [number, number, number, number] {
  const [yMin, xMin, yMax, xMax] = box;
  const normalizedRotation = ((rotation % 360) + 360) % 360;

  switch (normalizedRotation) {
    case 90:
      return [xMin, CANVAS_SIZE - yMax, xMax, CANVAS_SIZE - yMin];
    case 180:
      return [
        CANVAS_SIZE - yMax,
        CANVAS_SIZE - xMax,
        CANVAS_SIZE - yMin,
        CANVAS_SIZE - xMin,
      ];
    case 270:
      return [CANVAS_SIZE - xMax, yMin, CANVAS_SIZE - xMin, yMax];
    default:
      return [yMin, xMin, yMax, xMax];
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w, y + radius, radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h, x, y + h - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

const TextHighlightOverlayComponent: FC<TextHighlightOverlayProps> = ({
  matches,
  rotation,
  mode = "exact",
  transform,
  transformOrigin,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const renderOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    if (mode === "context") {
      ctx.filter = `blur(${BOX_BLUR_PX}px)`;
      matches.forEach((match) => {
        const [yMin, xMin, yMax, xMax] = transformBoxForRotation(
          match.box_2d,
          rotation
        );
        const left = xMin;
        const top = yMin;
        const width = xMax - xMin;
        const height = yMax - yMin;

        roundRect(ctx, left, top, width, height, BOX_BORDER_RADIUS);
        ctx.fillStyle = BOX_FILL;
        ctx.fill();
        ctx.lineWidth = BOX_LINE_WIDTH;
        ctx.strokeStyle = BOX_STROKE;
        ctx.stroke();
      });
      ctx.filter = "none";
    } else {
      ctx.globalCompositeOperation = "lighter";

      matches.forEach((match) => {
        const [yMin, xMin, yMax, xMax] = transformBoxForRotation(
          match.box_2d,
          rotation
        );
        const left = xMin;
        const top = yMin;
        const width = xMax - xMin;
        const height = yMax - yMin;
        const centerX = left + width / 2;
        const centerY = top + height / 2;
        const radius = Math.max(width, height) * HEATMAP_RADIUS_MULTIPLIER;

        const gradient = ctx.createRadialGradient(
          centerX,
          centerY,
          0,
          centerX,
          centerY,
          radius
        );
        HEATMAP_GRADIENT_STOPS.forEach(([offset, color]) => {
          gradient.addColorStop(offset, color);
        });

        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      });

      ctx.globalCompositeOperation = "source-over";
    }
  }, [matches, rotation, mode]);

  useEffect(() => {
    renderOverlay();
  }, [renderOverlay]);

  if (matches.length === 0) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_SIZE}
      height={CANVAS_SIZE}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{
        transform,
        transformOrigin,
      }}
    />
  );
};

export const TextHighlightOverlay = memo(TextHighlightOverlayComponent);
TextHighlightOverlay.displayName = "TextHighlightOverlay";
